import React, { Suspense } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { Amplify } from "aws-amplify";
import { HelmetProvider } from "react-helmet-async";
import routes from "./Config/routes.js";
import {
  AuthProvider,
  BannerProvider,
  CheckoutProvider,
  ConsultationProvider,
  ForumProvider,
  NewsProvider,
  NotificationProvider,
  PointProvider,
  SettingsProvider,
  SubscriptionProvider,
  WebinarProvider,
} from "./Context";
import awsconfig from "./aws-exports";
import RequireAuth from "./Components/RequireAuth";
import Header from "./Components/Header/index.js";
import Footer from "./Components/Footer/index.js";
import { useTranslation } from "react-i18next";
import { NPWPProvider } from "./Context/npwp/context.js";
import { EmeteraiProvider } from "./Context/emeterai/context.js";
import Spinner from "./Components/Spinner/index.js";
import MetaPixel from "./Components/MetaPixel/index.js";
import CookieConsent from "./Components/CookieConsent/index.js";
import { RemoteConfigProvider } from "./Context/firebase/context.js";

require("dayjs/locale/id");

const isLocalhost = Boolean(
  window.location.hostname === "localhost" ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === "[::1]" ||
    // 127.0.0.1/8 is considered localhost for IPv4.
    window.location.hostname.match(
      /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
    )
);

const isDev = Boolean(
  window.location.hostname === "web-user-v2-pajakind.vercel.app"
);

const [localRedirectSignIn, devRedirectSignIn, productionRedirectSignIn] =
  awsconfig.oauth.redirectSignIn.split(",");

const [localRedirectSignOut, devRedirectSignOut, productionRedirectSignOut] =
  awsconfig.oauth.redirectSignOut.split(",");

if (isLocalhost) {
  Amplify.configure({
    ...awsconfig,
    oauth: {
      ...awsconfig.oauth,
      redirectSignIn: localRedirectSignIn,
      redirectSignOut: localRedirectSignOut,
    },
  });
}

if (isDev) {
  Amplify.configure({
    ...awsconfig,
    oauth: {
      ...awsconfig.oauth,
      redirectSignIn: devRedirectSignIn,
      redirectSignOut: devRedirectSignOut,
    },
  });
}

if (!isLocalhost && !isDev) {
  Amplify.configure({
    ...awsconfig,
    oauth: {
      ...awsconfig.oauth,
      redirectSignIn: productionRedirectSignIn,
      redirectSignOut: productionRedirectSignOut,
    },
  });
}

function App() {
  const { t } = useTranslation();
  return (
    <HelmetProvider>
      <SettingsProvider>
        <AuthProvider>
          <RemoteConfigProvider>
            <NewsProvider>
              <BannerProvider>
                <NotificationProvider>
                  <ConsultationProvider>
                    <ForumProvider>
                      <NPWPProvider>
                        <EmeteraiProvider>
                          <PointProvider>
                            <SubscriptionProvider>
                              <CheckoutProvider>
                                <WebinarProvider>
                                  <MetaPixel />
                                  <CookieConsent />
                                  <Router>
                                    <Suspense fallback={<Spinner />}>
                                      <Routes>
                                        {routes.map((route) => {
                                          const Component = route.element;
                                          if (route.embed) {
                                            return (
                                              <Route
                                                key={route.path}
                                                path={route.path}
                                                element={
                                                  <RequireAuth
                                                    required={route.protected}
                                                  >
                                                    <Component />
                                                  </RequireAuth>
                                                }
                                              />
                                            );
                                          }
                                          return (
                                            <Route
                                              key={route.path}
                                              path={route.path}
                                              element={
                                                <RequireAuth
                                                  required={route.protected}
                                                >
                                                  <Header title={route.title} />
                                                  <main className="flex-auto">
                                                    {route.title &&
                                                    !route.hideTitle ? (
                                                      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                                                        <dl className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
                                                          <div className="sm:col-span-1">
                                                            <h1 className="text-xl font-bold text-gray-900">
                                                              {t(route.title)}
                                                            </h1>
                                                          </div>
                                                        </dl>
                                                      </div>
                                                    ) : null}
                                                    <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 pb-6 min-h-full">
                                                      <Component />
                                                    </div>
                                                  </main>
                                                  <Footer />
                                                </RequireAuth>
                                              }
                                            />
                                          );
                                        })}
                                      </Routes>
                                    </Suspense>
                                  </Router>
                                </WebinarProvider>
                              </CheckoutProvider>
                            </SubscriptionProvider>
                          </PointProvider>
                        </EmeteraiProvider>
                      </NPWPProvider>
                    </ForumProvider>
                  </ConsultationProvider>
                </NotificationProvider>
              </BannerProvider>
            </NewsProvider>
          </RemoteConfigProvider>
        </AuthProvider>
      </SettingsProvider>
    </HelmetProvider>
  );
}

export default App;
